import { BlogPost } from "@/types/blog";
import { RefObject, useEffect } from "react";
import { sanitizeHtml } from "@/utils/htmlUtils";
import { transformBlogUrls } from "@/utils/urlTransformer";
import { ServerBlogList } from "@/components/blog/ServerBlogList";
import { ServerFeaturedPost } from "@/components/blog/ServerFeaturedPost";
import { InfiniteScrollLoader } from "@/components/blog/InfiniteScrollLoader";
import { useServerSideBlogPosts } from "@/hooks/blog/useServerSideBlogPosts";

interface ServerBlogContentSectionProps {
  posts: BlogPost[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  searchTerm: string;
  selectedCategoryName: string;
  selectedCategoryId: number | null;
  onResetFilters: () => void;
  loaderRef: RefObject<HTMLDivElement>;
  isCategoryChanging?: boolean;
}

/**
 * Server-side safe version of BlogContentSection
 * This component is optimized for server-side rendering and ensures
 * that blog content is fully rendered in the initial HTML
 */
export const ServerBlogContentSection = ({
  posts: propPosts,
  loading: propLoading,
  loadingMore,
  hasMore,
  searchTerm,
  selectedCategoryName,
  selectedCategoryId,
  onResetFilters,
  loaderRef,
  isCategoryChanging = false
}: ServerBlogContentSectionProps) => {
  // Utilizziamo direttamente i post dalle props per il SEO
  const posts = propPosts;
  const loading = propLoading;

  // Log per debug
  useEffect(() => {
    console.log(`[ServerBlogContentSection] Rendering con ${posts.length} post (Props: ${propPosts.length})`);
  }, [posts.length, propPosts.length]);

  // Mostra stato di caricamento quando:
  // - caricamento iniziale senza post
  // - cambio categoria attivo
  // - oppure quando loading è true e non ci sono post
  const shouldShowLoading = (loading && posts.length === 0) || isCategoryChanging;

  // Funzione per generare i dati strutturati JSON-LD per i post del blog
  const generateStructuredData = (postsToRender: BlogPost[]) => {
    try {
      // Verifica che postsToRender sia un array valido
      if (!Array.isArray(postsToRender)) {
        console.error('[ServerBlogContentSection] postsToRender non è un array valido:', postsToRender);
        return JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": []
        });
      }

      // Crea un oggetto JSON-LD per la lista di articoli del blog
      const blogListingData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": postsToRender.map((post, index) => {
          try {
            // Verifica che post sia un oggetto valido
            if (!post || typeof post !== 'object') {
              console.error('[ServerBlogContentSection] post non è un oggetto valido:', post);
              return null;
            }

            // Estrai l'excerpt pulito (senza tag HTML)
            const cleanExcerpt = post.excerpt ? post.excerpt.replace(/<[^>]+>/g, '') : '';

            return {
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "BlogPosting",
                "headline": post.title || '',
                "description": cleanExcerpt,
                "author": {
                  "@type": "Person",
                  "name": post.author?.name || "Autore sconosciuto"
                },
                "datePublished": post.date || new Date().toISOString(),
                "dateModified": post.modified || post.date || new Date().toISOString(),
                "url": `https://gildy.it/blog/${post.slug || ''}`,
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://gildy.it/blog/${post.slug || ''}`
                },
                "image": post.featuredImage || "",
                "keywords": post.tags?.join(", ") || "",
                "articleSection": post.categories?.join(", ") || ""
              }
            };
          } catch (error) {
            console.error(`[ServerBlogContentSection] Errore nella generazione dei dati strutturati per il post ${index}:`, error);
            return null;
          }
        }).filter(Boolean) // Rimuovi gli elementi null
      };

      return JSON.stringify(blogListingData);
    } catch (error) {
      console.error('[ServerBlogContentSection] Errore nella generazione dei dati strutturati:', error);
      return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": []
      });
    }
  };

  // Renderizziamo il contenuto SEO qui per garantire che sia presente nella pagina
  const renderSEOArticles = () => {
    // Verifichiamo che i post siano un array valido
    const postsToRender = Array.isArray(posts) ? posts : [];
    
    // Log dopo aver determinato quali post renderizzare
    console.log(`[ServerBlogContentSection] Renderizzando ${postsToRender.length} post per SEO`);

    // Generiamo i dati strutturati solo se abbiamo post validi
    const structuredData = postsToRender.length > 0 
      ? generateStructuredData(postsToRender)
      : JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": []
        });

    return (
      <>
        {/* Script con dati strutturati JSON-LD per SEO */}
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: structuredData }}
        />
        
        {/* Contenuto SEO ottimizzato - solo metadati e excerpts */}
        <div className="seo-optimized-blog-content">
          {postsToRender.map(post => {
            if (!post) return null;

            try {
              // Estrai l'excerpt pulito (senza tag HTML)
              const cleanExcerpt = post.excerpt ? post.excerpt.replace(/<[^>]+>/g, '') : '';

              return (
                <article 
                  key={post.id} 
                  className="blog-post-seo"
                  itemScope 
                  itemType="https://schema.org/BlogPosting"
                >
                  <link itemProp="mainEntityOfPage" href={`https://gildy.it/blog/${post.slug || ''}`} />
                  <meta itemProp="headline" content={post.title || ''} />
                  <meta itemProp="description" content={cleanExcerpt} />
                  <meta itemProp="datePublished" content={post.date || new Date().toISOString()} />
                  {post.modified && <meta itemProp="dateModified" content={post.modified} />}
                  {post.featuredImage && <meta itemProp="image" content={post.featuredImage} />}
                  
                  <span itemProp="author" itemScope itemType="https://schema.org/Person">
                    <meta itemProp="name" content={post.author?.name || 'Autore sconosciuto'} />
                  </span>
                  
                  <span itemProp="publisher" itemScope itemType="https://schema.org/Organization">
                    <meta itemProp="name" content="Gildy" />
                    <span itemProp="logo" itemScope itemType="https://schema.org/ImageObject">
                      <meta itemProp="url" content="https://gildy.it/logo.png" />
                    </span>
                  </span>
                  
                  {/* Contenuto visibile che corrisponde a ciò che l'utente vede */}
                  <h2>{post.title || 'Titolo non disponibile'}</h2>
                  <p>{cleanExcerpt || 'Contenuto non disponibile'}</p>
                  <div>
                    <p>Scritto da: {post.author?.name || 'Autore sconosciuto'}</p>
                    <p>Pubblicato il: {post.date ? new Date(post.date).toLocaleDateString('it-IT') : 'Data non disponibile'}</p>
                  </div>
                  
                  {/* Link canonico per evitare contenuti duplicati */}
                  <link rel="canonical" href={`https://gildy.it/blog/${post.slug || ''}`} />
                </article>
              );
            } catch (error) {
              console.error(`[ServerBlogContentSection] Errore nel rendering del post ${post?.id || 'sconosciuto'}:`, error);
              return null;
            }
          })}
          
          {/* Metadata for SEO crawlers */}
          <div className="blog-metadata">
            <p>Total posts: {postsToRender.length}</p>
            <p>Last updated: {new Date().toISOString()}</p>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {/* Contenuto SEO ottimizzato che corrisponde a ciò che l'utente vede */}
      {renderSEOArticles()}

      {/* Interfaccia utente visibile - stessa visualizzazione di prima */}
      <div className="client-rendered-content">
        {!shouldShowLoading && (
          <div>
            {/* Determina se mostrare un post in evidenza */}
            {!searchTerm && !selectedCategoryName && posts && posts.length > 0 && (
              <div className="mb-12">
                <ServerFeaturedPost post={posts[0]} />
              </div>
            )}

            {/* Titolo della sezione */}
            <h2 className="text-2xl font-bold mb-6">
              {searchTerm || selectedCategoryName
                ? `Risultati${selectedCategoryName ? ` per "${selectedCategoryName}"` : ""}${searchTerm ? ` con "${searchTerm}"` : ""}`
                : "Articoli Recenti"}
            </h2>

            {/* Lista dei post ottimizzata per il server */}
            <ServerBlogList
              posts={!searchTerm && !selectedCategoryName && posts && posts.length > 0
                ? posts.slice(1) // Escludi il primo post se è in evidenza
                : (posts || [])
              }
            />
          </div>
        )}

        {/* Mostra loader infinito solo se abbiamo post e non stiamo cambiando categoria */}
        {posts && posts.length > 0 && !isCategoryChanging && (
          <InfiniteScrollLoader
            ref={loaderRef}
            loading={loadingMore}
            hasMore={hasMore}
          />
        )}
      </div>
    </>
  );
};
